import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { getSvcSettingsClient } from "sj-ui-toolkit/src/client/svcSettings";
import { getPlaClient } from "sj-ui-toolkit/src/client/svcClients";
import { AppContext } from "@/context/AppContext";
import { loadAllAssignmentPolicies, getProcessingAssignments } from "@/api/assignments";
import type { AssignmentPolicyData } from "@/api/assignments";
import { AssignmentsTable } from "@/components/assignments/AssignmentsTable";
import type { PlaProgressData } from "@/components/assignments/AssignmentsTable";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 5000;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const computePlaProgress = (
  plas: Array<{
    approvedAt?: string | null;
    archivedAt?: string | null;
    lessons?: Array<{ completed?: boolean }>;
  }>
): PlaProgressData => {
  const approved = plas.filter(p => p.approvedAt && !p.archivedAt);
  const total = approved.length;
  let complete = 0;
  let inProgress = 0;
  let notStarted = 0;
  approved.forEach(pla => {
    const lessons = pla.lessons ?? [];
    const completedCount = lessons.filter(l => l.completed).length;
    if (lessons.length > 0 && completedCount === lessons.length) complete++;
    else if (completedCount > 0) inProgress++;
    else notStarted++;
  });
  return { total, complete, inProgress, notStarted };
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const Assignments = () => {
  useBreadcrumb({
    label: "Assignments",
    route: AdminAppRoutes.routes.assignments.route,
    display: "none",
    resetTrail: true,
  });

  const { showToast } = useToast();
  const { state } = useContext(AppContext);
  const { viewOnly } = state;

  const tenantName = getCurrentSelectedTenant() ?? "";

  const [assignmentPolicies, setAssignmentPolicies] = useState<AssignmentPolicyData[] | "loading">(
    "loading"
  );
  const [processingAssignmentIds, setProcessingAssignmentIds] = useState<Set<string>>(new Set());
  const [aspenAdaptEnabled, setAspenAdaptEnabled] = useState(false);
  const [plaProgress, setPlaProgress] = useState<PlaProgressData | null>(null);
  const [plaLoading, setPlaLoading] = useState(false);

  const hasLoadedRef = useRef(false);

  const refreshProcessingAssignments = useCallback(() => {
    setProcessingAssignmentIds(getProcessingAssignments());
  }, []);

  useEffect(() => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    let cancelled = false;

    const load = async () => {
      try {
        const policies = await loadAllAssignmentPolicies(tenantName);
        if (cancelled) return;
        setAssignmentPolicies(policies);
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load assignments:", error);
        showToast({ severity: "error", detail: "Failed to load assignments" });
        setAssignmentPolicies([]);
      }
    };

    const loadAdaptAndPla = async () => {
      try {
        const { promise } = getSvcSettingsClient().getTenantSettings(tenantName, "aspen");
        const data = await promise;
        if (cancelled) return;
        const adaptGroup = (data?.groups ?? []).find(g => g.groupName === "aspen.adapt");
        const adaptEnabled =
          (adaptGroup?.values?.["enableAspenAdapt"] as boolean | undefined) ?? false;
        setAspenAdaptEnabled(adaptEnabled);
        if (adaptEnabled) {
          setPlaLoading(true);
          try {
            const { data: plaData, error } = await getPlaClient().getTenantPLAs(tenantName);
            if (cancelled) return;
            if (error) throw error;
            setPlaProgress(computePlaProgress(plaData?.assignments ?? []));
          } catch (e) {
            if (cancelled) return;
            console.error("Error fetching PLA data:", e);
            setPlaProgress(null);
            showToast({
              severity: "error",
              detail: "Unable to fetch personalized lesson assignment data.",
            });
          } finally {
            if (!cancelled) setPlaLoading(false);
          }
        }
      } catch (e) {
        if (cancelled) return;
        console.error("Error fetching Aspen settings:", e);
      }
    };

    void load();
    void loadAdaptAndPla();

    refreshProcessingAssignments();
    const interval = setInterval(refreshProcessingAssignments, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []); // empty deps — intentional: one-time load on mount (hasLoadedRef guard)

  const isLoading = assignmentPolicies === "loading";

  return (
    <AssignmentsTable
      assignmentPolicies={isLoading ? [] : assignmentPolicies}
      isLoading={isLoading}
      viewOnly={viewOnly}
      processingAssignmentIds={processingAssignmentIds}
      aspenAdaptEnabled={aspenAdaptEnabled}
      plaProgress={plaProgress}
      plaLoading={plaLoading}
    />
  );
};

export default Assignments;
